import React, { useMemo } from "react";
import Chip from "./Chip";
import { Filter, Vehicle } from "./data";

/** A customer slug as a label ("delta" -> "Delta"); shared with the detail rail's
    filter toggle so the two never spell a tenant differently. */
export const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface FilterChipsProps {
  vehicles: Vehicle[];
  filter: Filter;
  onFilter: (f: Filter) => void;
}

/**
 * Customer filter chips (All / AeroVect / Delta / …), derived from the roster.
 * Shared by the fleet grid and the per-tractor detail so the active filter is
 * visible AND changeable on both — it was previously only carried silently into
 * the detail (SW-6136).
 */
const FilterChips: React.FC<FilterChipsProps> = ({
  vehicles,
  filter,
  onFilter,
}) => {
  // Distinct customers present in the roster — drives the chips instead of a
  // hardcoded list.
  const customers = useMemo(() => {
    const set = new Set<string>();
    vehicles.forEach((v) => v.customer && set.add(v.customer));
    return Array.from(set).sort();
  }, [vehicles]);

  const countFor = (key: Filter) =>
    key === "all"
      ? vehicles.length
      : vehicles.filter((v) => v.customer === key).length;

  const defs: { key: Filter; label: string }[] = [
    { key: "all", label: `All (${countFor("all")})` },
    ...customers.map((c) => ({
      key: c,
      label: `${titleCase(c)} (${countFor(c)})`,
    })),
  ];

  return (
    <>
      {defs.map((f) => (
        <Chip
          key={f.key}
          tone="ink"
          active={filter === f.key}
          onClick={() => onFilter(f.key)}
        >
          {f.label}
        </Chip>
      ))}
    </>
  );
};

export default FilterChips;
